import tokenStorageService from "../../../services/TokenStorageService";
import gatewayService from '../../../services/GatewayService';
import * as CONSTANTS from '../../constants';
import {store} from '../../../services/StorageService';
import {eventTracker} from "../../../services/GoogleAnalyticsService";

/**
 * Get account activity for last 6 months.
 *
 * @param commit
 * @param payload
 * @returns {Promise<T>}
 */
export const getAccountActivity = ({ commit }, payload) => {
    commit(CONSTANTS.ACCOUNT_ACTIVITY_PENDING);
    const token = tokenStorageService.get();

    return gatewayService.getAccountActivity(payload, token).then((res) => {
        if(res.status === 200) {
            commit(CONSTANTS.ACCOUNT_ACTIVITY_SUCCESS, res.data.data);
        } else {
            commit(CONSTANTS.ACCOUNT_ACTIVITY_NO_CONTENT);
        }
    }).catch(() => {
        commit(CONSTANTS.ACCOUNT_ACTIVITY_FAILURE)
    })
};

/**
 * Get account notifications settings.
 *
 * @param commit
 * @param payload
 * @returns {Promise<T>}
 */
export const getAccountNotifications = ({ commit }) => {
    commit(CONSTANTS.ACCOUNT_NOTIFICATIONS_PENDING);
    const token = tokenStorageService.get();

    return gatewayService.getAccountNotifications(token).then((res) => {
        if(res.status === 200) {
            commit(CONSTANTS.ACCOUNT_NOTIFICATIONS_SUCCESS, res.data.data);
        } else {
            commit(CONSTANTS.ACCOUNT_NOTIFICATIONS_NO_CONTENT);
        }
    }).catch(() => {
        commit(CONSTANTS.ACCOUNT_NOTIFICATIONS_FAILURE)
    })
};

/**
 * Set the limit for the activity list. And reset the page count.
 *
 * @param commit
 * @param payload
 */
export const setRows = ({ commit }, payload) => {
    commit(CONSTANTS.ACCOUNT_ACTIVITY_ROWS_SET, payload);
    commit(CONSTANTS.ACCOUNT_ACTIVITY_PAGE_RESET);
};

/**
 * Increment the activity page.
 *
 * @param commit
 */
export const activityPageIncrement = ({ commit }) => {
    commit(CONSTANTS.ACCOUNT_ACTIVITY_PAGE_INCREMENT);
};

/**
 * Decrement the activity page.
 *
 * @param commit
 */
export const activityPageDecrement = ({ commit }) => {
    commit(CONSTANTS.ACCOUNT_ACTIVITY_PAGE_DECREMENT);
};

/**
 * Toggle the notification active on/off.
 *
 * @param commit
 * @param state
 * @param payload
 * @returns {Promise<T>}
 */
export const toggleNotifications = ({ commit, state }, payload) => {
    const user = state.user.user;
    const token = tokenStorageService.get();
    commit(CONSTANTS.ACCOUNT_NOTIFICATION_TOGGLE_PENDING);

    return gatewayService.toggleNotifications(token, user.id, payload).then((res) => {
        commit(CONSTANTS.ACCOUNT_NOTIFICATION_TOGGLE_SUCCESS, {
            data: res.data.data,
            // Google Analytics
            meta: eventTracker('account', 'notifications', 'user toggled email notifications', user.id)
        });
        commit('alert/ALERT_COUNTDOWN_RESET', {
            message: 'notifications',
            level: 'success'
        }, {root: true});
        return getAccountNotifications({commit});
    }).catch(() => {
        commit('alert/ALERT_COUNTDOWN_RESET', {
            message: 'error',
            level: 'danger'
        }, {root: true});
        commit(CONSTANTS.ACCOUNT_NOTIFICATION_TOGGLE_FAILURE);
    })
};

/**
 * Update the notification level for a user.
 *
 * @param commit
 * @param rootState
 * @param payload
 */
export const updateNotificationsLevel = ({ commit, rootState }, payload) => {
    const token = tokenStorageService.get();
    const supplierId = rootState.auth.supplier.id;
    commit(CONSTANTS.ACCOUNT_NOTIFICATION_UPDATE_PENDING);

    return gatewayService.updateNotificationsLevel(
        supplierId,
        token,
        {id: payload.id, notificationObject: payload.item}
    ).then((res) => {
        commit(CONSTANTS.ACCOUNT_NOTIFICATION_UPDATE_SUCCESS, {
            admin: payload.admin,
            data: res.data.data,
            id: payload.id,
            // Google Analytics
            meta: eventTracker('account', 'notifications', 'user modified email notifications', payload.id)
        });
        commit('alert/ALERT_COUNTDOWN_RESET', {
            message: 'notifications',
            level: 'success'
        }, {root: true});
        commit(CONSTANTS.ACCOUNT_NOTIFICATION_EDIT_TOGGLE, false);
        return getAccountNotifications({commit});
    }).catch(() => {
        commit(CONSTANTS.ACCOUNT_NOTIFICATION_UPDATE_FAILURE);
        commit('alert/ALERT_COUNTDOWN_RESET', {
            message: 'error',
            level: 'danger'
        }, {root: true});
    })
};

/**
 * Updates the notifications object.
 *
 * @param commit
 * @param payload
 */
export const editNotificationsLevel = ({ commit }, payload) => {
    commit(CONSTANTS.ACCOUNT_NOTIFICATION_EDIT, payload);
};

/**
 * Toggle the notifications edit mode.
 *
 * @param commit
 */
export const toggleNotificationsEditMode = ({ commit }, payload) => {
    commit(CONSTANTS.ACCOUNT_NOTIFICATION_EDIT_TOGGLE, payload);
};

/**
 * Get account basic information.
 *
 * @param commit
 * @param payload
 * @returns {Promise<T>}
 */
export const getAccount = ({ commit }) => {
    commit(CONSTANTS.ACCOUNT_USER_PENDING);
    const token = tokenStorageService.get();

    return gatewayService.getAccount(token).then((res) => {
        if(res.status === 200) {
            commit(CONSTANTS.ACCOUNT_USER_SUCCESS, res.data.data);
            store('user', {firstName: res.data.data['firstName'], lastName: res.data.data['lastName']});
        } else {
            commit(CONSTANTS.ACCOUNT_USER_NO_CONTENT)
        }
    }).catch(() => {
        commit(CONSTANTS.ACCOUNT_USER_FAILURE)
    })
};

/**
 * Edit the user object.
 *
 * @param commit
 * @param payload
 */
export const editUser = ({ commit }, payload) => {
    commit(CONSTANTS.ACCOUNT_USER_EDIT, payload);
};

/**
 * Reset the user object.
 *
 * @param commit
 */
export const resetUser = ({ commit }) => {
    commit(CONSTANTS.ACCOUNT_USER_RESET);
    commit(CONSTANTS.ACCOUNT_PASSWORD_RESET);
};

/**
 * Update the user.
 *
 * @param commit
 * @param state
 */
export const updateUser = ({ commit, state }) => {
    commit(CONSTANTS.ACCOUNT_USER_UPDATE_PENDING);
    const token = tokenStorageService.get();
    const user = state.user.user;

    return gatewayService.updateUser(user, token).then((res) => {
        if(res.status === 200) {
            commit(CONSTANTS.ACCOUNT_USER_UPDATE_SUCCESS, {
                data: res.data.data,
                // Google Analytics
                meta: eventTracker('account', 'user', 'account details was updated', user.id)
            });
            commit('auth/AUTH_USER_SET', res.data.data, {root: true});
            commit('alert/ALERT_COUNTDOWN_RESET', {
                message: 'updated',
                level: 'success'
            }, {root: true});
            store('user', {firstName: res.data.data['firstName'], lastName: res.data.data['lastName']});
        } else {
            commit(CONSTANTS.ACCOUNT_USER_UPDATE_NO_CONTENT)
        }
    }).catch(() => {
        commit(CONSTANTS.ACCOUNT_USER_UPDATE_FAILURE);
        commit('alert/ALERT_COUNTDOWN_RESET', {
            message: 'error',
            level: 'danger'
        }, {root: true});
    })
};



/**
 * Edit the new password objects.
 *
 * @param commit
 * @param payload
 */
export const editPassword = ({ commit }, payload) => {
    commit(CONSTANTS.ACCOUNT_PASSWORD_EDIT, payload);
};

/**
 * Reset the password fields.
 *
 * @param commit
 */
export const resetPassword = ({ commit }) => {
    commit(CONSTANTS.ACCOUNT_PASSWORD_RESET);
};

/**
 * Update the account password.
 *
 * @param commit
 * @param state
 */
export const updatePassword = ({ commit, state }) => {
    commit(CONSTANTS.ACCOUNT_PASSWORD_UPDATE_PENDING);
    const oldPassword = state.password.password;
    const newPassword = state.password.newPassword;
    const user = state.user.user;
    const token = tokenStorageService.get();

    return gatewayService.updatePassword(oldPassword, newPassword, token).then((res) => {
        commit(CONSTANTS.ACCOUNT_PASSWORD_RESET);
        if(res.status === 200) {
            commit(CONSTANTS.ACCOUNT_PASSWORD_UPDATE_SUCCESS, {
                // Google Analytics
                meta: eventTracker('account', 'password', 'account password updated', user.id)
            });
            commit('auth/AUTH_USER_SET', res.data.data, {root: true});
            commit('alert/ALERT_COUNTDOWN_RESET', {
                message: 'password',
                level: 'success'
            }, {root: true});
            store('user', {firstName: res.data.data['firstName'], lastName: res.data.data['lastName']});
        } else {
            commit(CONSTANTS.ACCOUNT_PASSWORD_UPDATE_NO_CONTENT)
        }
    }).catch((err) => {
        commit(CONSTANTS.ACCOUNT_PASSWORD_RESET);
        commit(CONSTANTS.ACCOUNT_PASSWORD_UPDATE_FAILURE);
        if(err.status === 403) {
            commit('alert/ALERT_COUNTDOWN_RESET', {
                message: 'wrongPassword',
                level: 'danger'
            }, {root: true});
        } else {
            commit('alert/ALERT_COUNTDOWN_RESET', {
                message: 'error',
                level: 'danger'
            }, {root: true});
        }
    })
};



// WEBPACK FOOTER //
// ./src/store/modules/account/actions.js